import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Menu as MenuIcon, ChevronRight } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const MenuItems = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();

  const [menuItems, setMenuItems] = useState([]);
  const [allMenus, setAllMenus] = useState([]); // Changed: Store all menus for parent dropdown
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    menuid: null,
    menucode: '',
    menuname: '',
    parentmenuid: '',
    iconname: '',
    menuorder: 0,
    isactive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Common icon options for lucide-react
  const iconOptions = [
    'Home', 'Database', 'Package', 'FileText', 'Settings',
    'Users', 'User', 'Globe', 'MapPin', 'Building',
    'DollarSign', 'Truck', 'Plane', 'Ship', 'Send',
    'BarChart', 'PieChart', 'List', 'Grid', 'Layout',
    'Folder', 'File', 'Calendar', 'Clock', 'Bell',
    'Mail', 'Phone', 'Camera', 'Image', 'Video',
    'Music', 'Mic', 'Speaker', 'Monitor', 'Smartphone',
    'Tablet', 'Laptop', 'Printer', 'Save', 'Download',
    'Upload', 'Cloud', 'Lock', 'Unlock', 'Key',
    'Shield', 'AlertCircle', 'Info', 'CheckCircle', 'XCircle'
  ];

  useEffect(() => {
    fetchMenuItems();
  }, []);

  useEffect(() => {
    filterMenuItems();
  }, [searchTerm, menuItems]);

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      // Fetch all active menus (no parent-only filter)
      const response = await apiClient.get('/MenuItems.php', {
        params: { active: true }
      });
      
      if (response.data.success) {
        const menus = response.data.data || [];
        setMenuItems(menus);
        setAllMenus(menus); // Store all menus for parent dropdown
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterMenuItems = () => {
    if (!searchTerm.trim()) {
      setFilteredMenuItems(menuItems);
    } else {
      const filtered = menuItems.filter(item =>
        item.menuname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.menucode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.parentmenuname?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMenuItems(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.menucode.trim()) {
      newErrors.menucode = 'Menu code is required';
    } else if (formData.menucode.length > 50) {
      newErrors.menucode = 'Menu code must be 50 characters or less';
    }
    
    if (!formData.menuname.trim()) {
      newErrors.menuname = 'Menu name is required';
    } else if (formData.menuname.length > 100) {
      newErrors.menuname = 'Menu name must be 100 characters or less';
    }
    
    if (formData.iconname && formData.iconname.length > 50) {
      newErrors.iconname = 'Icon name must be 50 characters or less';
    }
    
    // Prevent circular reference
    if (editMode && formData.parentmenuid && formData.parentmenuid === formData.menuid) {
      newErrors.parentmenuid = 'A menu cannot be its own parent';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/MenuItems.php', {
        ...formData,
        parentmenuid: formData.parentmenuid !== '' ? parseInt(formData.parentmenuid) : null,
        menuorder: parseInt(formData.menuorder) || 0,
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'Menu item updated successfully!' : 'Menu item created successfully!');
        setShowModal(false);
        resetForm();
        fetchMenuItems();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving menu item:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('Menu code already exists. Please use a different code.');
      } else {
        alert('Error saving menu item. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (menuItem) => {
    setFormData({
      menuid: menuItem.menuid,
      menucode: menuItem.menucode,
      menuname: menuItem.menuname,
      parentmenuid: menuItem.parentmenuid || '',
      iconname: menuItem.iconname || '',
      menuorder: menuItem.menuorder,
      isactive: menuItem.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id, hasChildren) => {
    if (hasChildren) {
      alert('Cannot delete menu with submenus. Please delete or reassign submenus first.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this menu item?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/MenuItems.php', {
        data: { menuid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Menu item deleted successfully!');
        fetchMenuItems();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting menu item:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('Cannot delete menu with submenus.');
      } else {
        alert('Error deleting menu item. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      menuid: null,
      menucode: '',
      menuname: '',
      parentmenuid: '',
      iconname: '',
      menuorder: 0,
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Check if menu has children
  const hasChildren = (menuId) => {
    return menuItems.some(item => item.parentmenuid === menuId);
  };

  // Get available parent menus (all menus except current one and its descendants)
  const getAvailableParentMenus = () => {
    if (!editMode) {
      // When adding new menu, show all menus
      return allMenus;
    }
    
    // When editing, exclude the current menu and its descendants to prevent circular references
    const currentMenuId = formData.menuid;
    
    // Find all descendant IDs recursively
    const getDescendantIds = (menuId) => {
      const children = allMenus.filter(m => m.parentmenuid === menuId);
      let descendants = children.map(c => c.menuid);
      children.forEach(child => {
        descendants = [...descendants, ...getDescendantIds(child.menuid)];
      });
      return descendants;
    };
    
    const excludedIds = [currentMenuId, ...getDescendantIds(currentMenuId)];
    
    return allMenus.filter(menu => !excludedIds.includes(menu.menuid));
  };

  // Group menus by parent for better display
  const groupMenusByParent = (menus) => {
    const parents = menus.filter(m => !m.parentmenuid);
    const children = menus.filter(m => m.parentmenuid);
    
    return parents.map(parent => ({
      ...parent,
      children: children.filter(c => c.parentmenuid === parent.menuid)
    }));
  };

  const availableParentMenus = getAvailableParentMenus();
  const groupedMenus = groupMenusByParent(availableParentMenus);

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>
            <MenuIcon size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            Menu Items Master
          </h2>
          <p className="subtitle">Manage navigation menu structure and hierarchy</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Menu Item
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by menu name, code, or parent menu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredMenuItems.length} {filteredMenuItems.length === 1 ? 'menu' : 'menus'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading menu items...</div>
      ) : filteredMenuItems.length === 0 ? (
        <div className="empty-state">
          <p>No menu items found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ minWidth: '100%', tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={{ minWidth: '120px' }}>Menu Code</th>
                <th style={{ minWidth: '150px' }}>Menu Name</th>
                <th style={{ minWidth: '150px' }}>Parent Menu</th>
                <th style={{ minWidth: '100px' }}>Icon</th>
                <th style={{ minWidth: '80px', textAlign: 'center' }}>Order</th>
                <th style={{ minWidth: '100px' }}>Status</th>
                <th style={{ minWidth: '120px' }}>Created At</th>
                <th style={{ minWidth: '140px', textAlign: 'center', position: 'sticky', right: 0, backgroundColor: '#764ba2' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMenuItems.map((menuItem) => (
                <tr key={menuItem.menuid}>
                  <td className="font-mono">{menuItem.menucode}</td>
                  <td className="font-semibold">
                    {menuItem.parentmenuid && (
                      <ChevronRight size={16} style={{ display: 'inline', marginRight: '5px', color: '#9CA3AF' }} />
                    )}
                    {menuItem.menuname}
                  </td>
                  <td>
                    {menuItem.parentmenuname ? (
                      <span className="country-badge">
                        {menuItem.parentmenucode} - {menuItem.parentmenuname}
                      </span>
                    ) : (
                      <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Main Menu</span>
                    )}
                  </td>
                  <td className="font-mono">{menuItem.iconname || '-'}</td>
                  <td style={{ textAlign: 'center' }}>{menuItem.menuorder}</td>
                  <td>
                    <span className={`status-badge ${menuItem.isactive ? 'active' : 'inactive'}`}>
                      {menuItem.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(menuItem.createdat).toLocaleDateString()}</td>
                  <td style={{ 
                    textAlign: 'center', 
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    right: 0,
                    backgroundColor: 'white',
                    boxShadow: '-4px 0 6px -2px rgba(0, 0, 0, 0.1)'
                  }}>
                    <button
                      onClick={() => handleEdit(menuItem)}
                      title="Edit"
                      style={{
                        padding: '8px 12px',
                        marginRight: '8px',
                        border: '2px solid #667eea',
                        borderRadius: '8px',
                        backgroundColor: '#ede9fe',
                        color: '#667eea',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        fontWeight: '600'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#667eea';
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ede9fe';
                        e.currentTarget.style.color = '#667eea';
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(menuItem.menuid, hasChildren(menuItem.menuid))}
                      title="Delete"
                      style={{
                        padding: '8px 12px',
                        border: '2px solid #dc2626',
                        borderRadius: '8px',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        fontWeight: '600'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#dc2626';
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                        e.currentTarget.style.color = '#dc2626';
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Menu Item' : 'Add New Menu Item'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="menucode">
                    Menu Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="menucode"
                    value={formData.menucode}
                    onChange={(e) => setFormData({ ...formData, menucode: e.target.value.toUpperCase() })}
                    maxLength="50"
                    placeholder="e.g., CUSTOMERS, REPORTS, SETTINGS"
                    className={errors.menucode ? 'error' : ''}
                  />
                  {errors.menucode && (
                    <span className="error-message">{errors.menucode}</span>
                  )}
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Enter unique code (e.g., VEHICLES, USER_MANAGEMENT)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="menuname">
                    Menu Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="menuname"
                    value={formData.menuname}
                    onChange={(e) => setFormData({ ...formData, menuname: e.target.value })}
                    maxLength="100"
                    placeholder="e.g., Dashboard, Reports"
                    className={errors.menuname ? 'error' : ''}
                  />
                  {errors.menuname && (
                    <span className="error-message">{errors.menuname}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="parentmenuid">
                    Parent Menu
                    {editMode && formData.menuid && (
                      <small style={{ color: '#6B7280', marginLeft: '8px', fontWeight: 'normal' }}>
                        (Circular references prevented)
                      </small>
                    )}
                  </label>
                  <select
                    id="parentmenuid"
                    value={formData.parentmenuid}
                    onChange={(e) => setFormData({ ...formData, parentmenuid: e.target.value })}
                    className={errors.parentmenuid ? 'error' : ''}
                  >
                    <option value="">-- None (Main Menu) --</option>
                    
                    {/* Show grouped menus with indentation */}
                    {groupedMenus.map(parent => (
                      <optgroup key={parent.menuid} label={`${parent.menucode} - ${parent.menuname}`}>
                        <option value={parent.menuid}>
                          {parent.menucode} - {parent.menuname}
                        </option>
                        {parent.children.map(child => (
                          <option key={child.menuid} value={child.menuid}>
                            &nbsp;&nbsp;↳ {child.menucode} - {child.menuname}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {errors.parentmenuid && (
                    <span className="error-message">{errors.parentmenuid}</span>
                  )}
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    You can nest menus multiple levels deep
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="iconname">Icon Name (lucide-react)</label>
                  <select
                    id="iconname"
                    value={formData.iconname}
                    onChange={(e) => setFormData({ ...formData, iconname: e.target.value })}
                    className={errors.iconname ? 'error' : ''}
                  >
                    <option value="">-- No Icon --</option>
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                  {errors.iconname && (
                    <span className="error-message">{errors.iconname}</span>
                  )}
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Icons from lucide-react library
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="menuorder">Display Order</label>
                  <input
                    type="number"
                    id="menuorder"
                    value={formData.menuorder}
                    onChange={(e) => setFormData({ ...formData, menuorder: parseInt(e.target.value) || 0 })}
                    min="0"
                    placeholder="0"
                  />
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Lower numbers appear first
                  </small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isactive}
                      onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuItems;